import React from 'react';

// Constellation
import { Anchor, Heading, List, Paragraph } from '@zillow/constellation-10';

const StaticCommonQuestions = () => {
  return (
    <>
      <Heading level={2} textStyle="responsive.heading-md" css={{ marginBottom: '600' }}>
        Common questions about renting your home
      </Heading>
      <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '200' }}>
        1. How should I price my rental?
      </Heading>
      <Paragraph css={{ marginBottom: '600' }}>
        To determine your rent price, consider local rent control laws, the rental rates of homes in your area (rental
        comps), the features of your home, and changes in your local market. How much you charge for rent is ultimately
        up to you, but to get a quick starting point, try our free Rent Zestimate<sup>®</sup> tool. To learn more about
        pricing your rental, check out this{' '}
        <Anchor
          href="https://www.zillow.com/rental-manager/resources/how-much-can-i-rent-my-house-for/"
          target="_blank"
          rel="noopener noreferrer"
        >
          article
        </Anchor>
        .
      </Paragraph>
      <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '200' }}>
        2. How is my Rent Zestimate<sup>®</sup> calculated?
      </Heading>
      <Paragraph>
        The Rent Zestimate<sup>®</sup> tool helps provide a rent estimate by address. To come up with the Zillow Rent
        Zestimate
        <sup>®</sup>, we look at publicly available data to determine:
      </Paragraph>
      <List.Root appearance="bullet" css={{ marginBottom: '600' }}>
        <List.Item>
          The home&apos;s physical attributes and amenities (like square footage and number of bedrooms and bathrooms)
        </List.Item>
        <List.Item>Comparable rental properties and the market rental rates in the area</List.Item>
        <List.Item>Any owner-updated home facts, plus other public data like the last sale price</List.Item>
      </List.Root>
      <Paragraph css={{ marginBottom: '600' }}>
        Remember that this is just a rent estimate — it&apos;s not set in stone but serves as a resource for landlords
        and property managers. Learn more about the Rent Zestimate<sup>®</sup>{' '}
        <Anchor
          href="https://www.zillow.com/rental-manager/resources/what-is-the-rent-zestimate/"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </Anchor>
        .
      </Paragraph>
      <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '200' }}>
        3. How can I advertise my rental?
      </Heading>
      <Paragraph css={{ marginBottom: '600' }}>
        Most renters search online when looking for a home, so it&apos;s important to maximize your listing&apos;s
        exposure across multiple brands. With Zillow Rental Manager, you can{' '}
        <Anchor href="https://www.zillow.com/rental-manager/post-a-listing/" target="_blank" rel="noopener noreferrer">
          advertise your rental listings
        </Anchor>{' '}
        across three of the top five rental networks — Zillow, Trulia, and HotPads.* exposing your rental listing to
        over 30 million monthly users.**
      </Paragraph>
      <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '200' }}>
        4. What should I include in my rental listing?
      </Heading>
      <Paragraph>Some key elements to include in your listing are:</Paragraph>
      <List.Root appearance="bullet" css={{ marginBottom: '600' }}>
        <List.Item>
          A detailed rental property description (including square footage, number of bedrooms and bathrooms)
        </List.Item>
        <List.Item>Rent price, lease duration, and required fees</List.Item>
        <List.Item>At least 10 high-quality photos</List.Item>
        <List.Item>
          Elevate your listing with a 3D Home<sup>®</sup> tour - easily include a 3D virtual tour using the free{' '}
          <Anchor href="https://www.zillow.com/marketing/3d-home/" target="_blank" rel="noopener noreferrer">
            Zillow 3D Home<sup>®</sup> app
          </Anchor>
        </List.Item>
        <List.Item>Parking and pet restrictions</List.Item>
        <List.Item>Shared amenities</List.Item>
        <List.Item>Proximity to transit, dining, shopping, etc.</List.Item>
      </List.Root>
      <Paragraph css={{ marginBottom: '600' }}>
        Get more information about{' '}
        <Anchor
          href="https://www.zillow.com/rental-manager/resources/how-to-write-a-rental-ad/"
          target="_blank"
          rel="noopener noreferrer"
        >
          writing your listing here
        </Anchor>
        .
      </Paragraph>
      <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '200' }}>
        5. When should I raise or lower my rent price?
      </Heading>
      <Paragraph css={{ marginBottom: '600' }}>
        As a landlord, you may consider increasing your rent if market rates, property taxes, insurance premiums, or
        homeowners association fees have increased. Also, consider if there are property maintenance expenses that need
        to be covered.{' '}
        <Anchor
          href="https://www.zillow.com/rental-manager/resources/raising-rent/"
          target="_blank"
          rel="noopener noreferrer"
        >
          This guide
        </Anchor>{' '}
        can help you make your decision.
      </Paragraph>
      <Paragraph css={{ marginBottom: '800' }}>
        If you&apos;re struggling to find renters, you may consider lowering your rental price. You may also try
        rethinking your terms (i.e. offering short-term leases) or offering a discount on the first month&apos;s rent.
        Just make sure you&apos;re aware of what your budget can accommodate.
      </Paragraph>
      <Paragraph textStyle="body-xs" css={{ marginBottom: '200' }}>
        <em>
          *Source: Comscore Media Metrix<sup>®</sup> Multi-Platform Key Measures, Custom-defined list including:
          Zillow.com Rentals, Trulia.com Rentals, HotPads.com, Apartments.com, Rent.com, Apartmentguide.com,
          Apartmenthomeliving.com, Forrent.com, Apartmentfinder.com, Rentals.com, Total Unique Visitors, December 2019
        </em>
      </Paragraph>
      <Paragraph textStyle="body-xs" css={{ marginBottom: '200' }}>
        <em>**Source: Zillow Internal Data, 2021</em>
      </Paragraph>
    </>
  );
};

export default StaticCommonQuestions;
